import {
  completeManagedHermesOAuth,
  rescanHermesGrokStatus,
  startManagedHermesOAuth,
  startManagedHermesRuntime,
} from "../../lib/hermes-grok";
import type { AiProviderId, OnboardingSurface } from "../../lib/onboarding-v2";

interface ConnectGrokInput {
  finishProvider: (
    provider: "hermes-grok",
    status: "connected",
  ) => Promise<void>;
  isDesktopSurface: boolean;
  setMessage: (value: string | null) => void;
  setOauthCommand: (value: string | null) => void;
  surface: OnboardingSurface;
}

interface SaveKeyInput {
  apiBase: string;
  apiKey: string;
  finishProvider: (provider: AiProviderId, status: "key-set") => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  provider: AiProviderId;
}

export async function connectGrokProvider(input: ConnectGrokInput) {
  input.setOauthCommand(null);
  if (input.isDesktopSurface) {
    if (!window.electron?.runShellCommand)
      throw new Error("Open Fintheon Desktop to connect local Hermes Grok.");
    await window.electron.runShellCommand("hermes model");
    const status = await rescanHermesGrokStatus();
    if (!status.available)
      throw new Error(status.detail || "Grok is not connected yet.");
    await input.finishProvider("hermes-grok", "connected");
    return;
  }
  if (input.surface !== "web")
    throw new Error("Complete Grok OAuth from Desktop or Web first.");
  await startManagedHermesRuntime();
  const started = await startManagedHermesOAuth();
  input.setOauthCommand(started.command);
  input.setMessage("Run the Hermes OAuth command, then confirm completion.");
}

export async function confirmManagedGrokProvider(
  finishProvider: (
    provider: "hermes-grok",
    status: "connected",
  ) => Promise<void>,
) {
  const runtime = await completeManagedHermesOAuth();
  if (runtime.oauthStatus !== "connected")
    throw new Error("Hermes OAuth is still pending.");
  await finishProvider("hermes-grok", "connected");
}

export async function saveProviderApiKey(input: SaveKeyInput) {
  const trimmedKey = input.apiKey.trim();
  if (trimmedKey.length < 10)
    throw new Error("Enter a valid provider API key.");
  const token = await input.getAccessToken();
  const res = await fetch(
    `${input.apiBase.replace(/\/$/, "")}/api/settings/ai-keys`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        apiKey: trimmedKey,
        provider:
          input.provider === "deepseek-direct" ? "deepseek" : "opencode-go",
      }),
    },
  );
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? "API key save failed.");
  await input.finishProvider(input.provider, "key-set");
}
